import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  getFavorites,
  getFavoritesWithRetailer,
  removeFavorite,
  removeFavoriteWithRetailer,
  addFavorite,
  addFavoriteWithRetailer,
  addWalmartOnlineFavorite,
  type FavoritesResponse,
  type AddFavoriteRequest,
  type AddWalmartOnlineFavoriteRequest,
} from "../services/FavoritesService";
import { toast } from "react-toastify";
import { useUserContext } from "../context/UserContext";
import { hasFavoritesPlanAccess } from "../utils/userUtils";
import { globalFavoritesQueryKey } from "../services/GlobalFavoritesService";

// Query keys
export const favoritesKeys = {
  all: ["favorites"] as const,
  lists: () => [...favoritesKeys.all, "list"] as const,
  list: (pageSize: number) => [...favoritesKeys.lists(), { pageSize }] as const,
  listWithRetailer: (pageSize: number, retailer: string, basic?: boolean) =>
    [...favoritesKeys.lists(), { pageSize, retailer, basic }] as const,
};

/**
 * Hook for fetching paginated favorites with infinite query
 */
export const useFavorites = (pageSize: number = 20) => {
  return useInfiniteQuery({
    queryKey: favoritesKeys.list(pageSize),
    queryFn: ({ pageParam }) => getFavorites(pageSize, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => {
      return lastPage?.hasMore ? lastPage.nextCursor : undefined;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Hook for removing a favorite with optimistic updates
 */
export const useRemoveFavorite = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: removeFavorite,
    onMutate: async (upc: string) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: favoritesKeys.lists() });

      // Snapshot the previous value
      const previousFavorites = queryClient.getQueryData<{
        pages: FavoritesResponse[];
        pageParams: (string | undefined)[];
      }>(favoritesKeys.list(20));

      // Optimistically update to remove the favorite
      queryClient.setQueryData<{
        pages: FavoritesResponse[];
        pageParams: (string | undefined)[];
      }>(favoritesKeys.list(20), (old) => {
        if (!old) return old;

        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            favorites: page.favorites.filter((fav) => fav.upc !== upc),
          })),
        };
      });

      // Return a context object with the snapshotted value
      return { previousFavorites };
    },
    onError: (_, __, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousFavorites) {
        queryClient.setQueryData(
          favoritesKeys.list(20),
          context.previousFavorites
        );
      }
      toast.error("Failed to remove from saved");
    },
    onSuccess: () => {
      toast.success("Removed from saved");
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey: favoritesKeys.lists() });
    },
  });
};

/**
 * Hook for adding a favorite
 */
export const useAddFavorite = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: addFavorite,
    onSuccess: () => {
      // Use invalidation to trigger a refetch for immediate UI update
      queryClient.invalidateQueries({ queryKey: favoritesKeys.lists() });
      // Also invalidate favorite check queries to update button states
      queryClient.invalidateQueries({ queryKey: favoritesKeys.all });
      toast.success("Added to saved");
    },
    onError: (error) => {
      const status =
        error && typeof error === "object" && "status" in error
          ? (error as { status?: number }).status
          : undefined;
      if (status === 403) return;
      toast.error(error instanceof Error ? error.message : "Failed to add to saved");
    },
  });
};

/**
 * Helper hook to get all favorites as a flat array
 */
export const useFlatFavorites = (pageSize: number = 20) => {
  const query = useFavorites(pageSize);

  const favorites = query.data?.pages.flatMap((page) => page?.favorites ?? []) ?? [];

  return {
    ...query,
    favorites,
  };
};

/**
 * Hook for fetching paginated favorites with infinite query for a specific retailer
 */
export const useFavoritesWithRetailer = (
  pageSize: number = 20,
  retailer: string = "walmart",
  basic: boolean = false,
  options?: {
    staleTime?: number;
    refetchOnMount?: boolean | "always";
    refetchOnWindowFocus?: boolean;
    enabled?: boolean;
    allowFreeOnline?: boolean;
  }
) => {
  const { user } = useUserContext();
  const queryKey = favoritesKeys.listWithRetailer(pageSize, retailer, basic);
  // Skip the request when the user has no plan that grants favorites for this
  // retailer (mirrors the server-side `hasPlanForRetailer` check) so we don't
  // spam the backend with 403s for users on _basic-only plans.
  const planAccess = hasFavoritesPlanAccess(user, retailer, options?.allowFreeOnline);
  const isEnabled = (options?.enabled ?? true) && !!user && planAccess;

  return useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) =>
      getFavoritesWithRetailer(pageSize, pageParam, retailer, basic),
    getNextPageParam: (lastPage) => lastPage?.nextCursor,
    initialPageParam: undefined as string | undefined,
    enabled: isEnabled,
    retry: 3,
    staleTime: options?.staleTime,
    refetchOnMount: options?.refetchOnMount,
    refetchOnWindowFocus: options?.refetchOnWindowFocus,
  });
};

/**
 * Hook for removing a favorite for a specific retailer
 */
export const useRemoveFavoriteWithRetailer = (
  retailer: string = "walmart",
  basic: boolean = false,
  showSuccessToast: boolean = true
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (upc: string) =>
      removeFavoriteWithRetailer(upc, retailer, basic),
    onSuccess: (data) => {
      // Update the query cache directly with the returned data
      if (data) {
        queryClient.setQueryData(
          favoritesKeys.listWithRetailer(100, retailer, basic),
          { pages: [data], pageParams: [undefined] }
        );
      }
      // Invalidate ALL favorites queries for this retailer to update counts everywhere
      queryClient.invalidateQueries({
        queryKey: ["favorites", "list"],
        predicate: (query) => {
          const key = query.queryKey as unknown[];
          return key.length >= 3 && (key[2] as { retailer?: string })?.retailer === retailer;
        },
      });
      // Refresh the global all-retailers favorites view (nav-bar modal)
      queryClient.invalidateQueries({ queryKey: globalFavoritesQueryKey });
      if (showSuccessToast) {
        toast.success("Removed from saved");
      }
    },
    onError: () => {
      toast.error("Failed to remove from saved");
    },
  });
};

/**
 * Hook for adding a favorite for a specific retailer
 */
export const useAddFavoriteWithRetailer = (
  retailer: string = "walmart",
  basic: boolean = false,
  showSuccessToast: boolean = true
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (productData: AddFavoriteRequest) =>
      addFavoriteWithRetailer(productData, retailer, basic),
    onSuccess: (data) => {
      // Update the query cache directly with the returned data
      if (data) {
        queryClient.setQueryData(
          favoritesKeys.listWithRetailer(100, retailer, basic),
          { pages: [data], pageParams: [undefined] }
        );
      }
      // Invalidate ALL favorites queries for this retailer to update counts everywhere
      queryClient.invalidateQueries({
        queryKey: ["favorites", "list"],
        predicate: (query) => {
          const key = query.queryKey as unknown[];
          return key.length >= 3 && (key[2] as { retailer?: string })?.retailer === retailer;
        },
      });
      // Refresh the global all-retailers favorites view (nav-bar modal)
      queryClient.invalidateQueries({ queryKey: globalFavoritesQueryKey });
      if (showSuccessToast) {
        toast.success("Added to saved");
      }
    },
    onError: (error) => {
      const status =
        error && typeof error === "object" && "status" in error
          ? (error as { status?: number }).status
          : undefined;
      if (status === 403) return;
      toast.error(error instanceof Error ? error.message : "Failed to add to saved");
    },
  });
};

/**
 * Hook for adding a walmart_online favorite with full product data
 */
export const useAddWalmartOnlineFavorite = (showSuccessToast: boolean = true) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (productData: AddWalmartOnlineFavoriteRequest) =>
      addWalmartOnlineFavorite(productData),
    onSuccess: (data) => {
      if (data) {
        queryClient.setQueryData(
          favoritesKeys.listWithRetailer(100, "walmart_online", true),
          { pages: [data], pageParams: [undefined] }
        );
      }
      // Invalidate ALL favorites queries for walmart_online to update counts everywhere
      queryClient.invalidateQueries({
        queryKey: ["favorites", "list"],
        predicate: (query) => {
          const key = query.queryKey as unknown[];
          return key.length >= 3 && (key[2] as { retailer?: string })?.retailer === "walmart_online";
        },
      });
      // Refresh the global all-retailers favorites view (nav-bar modal)
      queryClient.invalidateQueries({ queryKey: globalFavoritesQueryKey });
      if (showSuccessToast) {
        toast.success("Added to saved");
      }
    },
    onError: () => {
      toast.error("Failed to add to saved");
    },
  });
};

/**
 * Helper hook to get all favorites as a flat array for a specific retailer
 */
export const useFlatFavoritesWithRetailer = (
  pageSize: number = 20,
  retailer: string = "walmart",
  basic: boolean = false,
  options?: {
    staleTime?: number;
    refetchOnMount?: boolean | "always";
    refetchOnWindowFocus?: boolean;
    enabled?: boolean;
    allowFreeOnline?: boolean;
  }
) => {
  const query = useFavoritesWithRetailer(pageSize, retailer, basic, options);

  const favorites = query.data?.pages.flatMap((page) => page?.favorites ?? []) ?? [];

  return {
    ...query,
    favorites,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
  };
};
